/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { classes, VComponent } from '@adobe-commerce/elsie/lib';
import '@/quote-management/components/ActionsBar/ActionsBar.css';
import { Picker } from '@adobe-commerce/elsie/components';

export interface ActionsBarProps extends HTMLAttributes<HTMLDivElement> {
  dropdownPlaceholder?: string;
  dropdownOptions?: {
    label: string;
    value: string;
  }[];
  dropdownValue?: string;
  handleDropdownChange?: (event: Event) => void;
  buttons?: VNode[];
}

export const ActionsBar: FunctionComponent<ActionsBarProps> = ({
  className,
  dropdownPlaceholder,
  dropdownOptions,
  dropdownValue,
  handleDropdownChange,
  buttons,
  ...props
}) => {
  const hasDropdown = dropdownOptions && dropdownOptions.length > 0;
  const hasButtons = buttons && buttons.length > 0;

  const pickerOptions =
    dropdownOptions?.map((option) => ({
      text: option.label,
      value: option.value,
    })) || [];
  pickerOptions.unshift({
    text: dropdownPlaceholder || '',
    value: '',
  });

  return (
    <div
      className="quote-management-actions-bar__container"
      data-testid="actions-bar-container"
    >
      <div
        className={classes(['quote-management-actions-bar', className])}
        data-testid="actions-bar"
        {...props}
      >
        {hasDropdown && (
          <Picker
            key={`actions-bar-picker-${dropdownValue || 'empty'}`}
            name="dropdown"
            id="dropdown"
            handleSelect={handleDropdownChange}
            className="quote-management-actions-bar__dropdown"
            data-testid="dropdown"
            options={pickerOptions}
            value={dropdownValue || ''}
          />
        )}
        {hasButtons && (
          <div
            className="quote-management-actions-bar__buttons"
            data-testid="buttons-container"
          >
            {buttons?.map((button, index) => (
              <VComponent
                node={button}
                className={classes(['quote-management-actions-bar__button'])}
                key={index}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
